"use client";

import { useQuery } from "@tanstack/react-query";
import { Effect } from "effect";
import { RpcClient } from "@effect/rpc";
import { feedMediaEq, FileExtractionsRpc } from "./api/feed-media-client";

// URLs expire after 60 minutes; 30-minute staleTime keeps a safety margin.
const STALE_TIME_MS = 30 * 60 * 1000;
const GC_TIME_MS = 45 * 60 * 1000;

/**
 * Resolve a Cova Feed post image (file-extractions fileUuid) to a short-lived
 * presigned GET URL via the platform-wide, NON-owner-scoped feed-image path —
 * so every authenticated user can see the super-admin-authored image, not just
 * its uploader (which the generic owner-scoped resolver would restrict to).
 */
export function useFeedImageUrl(fileUuid: string | null | undefined) {
  const enabled = !!fileUuid;
  const {
    data,
    isLoading
  } = useQuery(feedMediaEq.queryOptions({
    queryKey: ["feed-image", fileUuid ?? ""],
    queryFn: () => Effect.gen(function* () {
      const rpcClient = yield* RpcClient.make(FileExtractionsRpc);
      return yield* rpcClient.resolveFeedImage({
        fileUuid: fileUuid!
      });
    }).pipe(Effect.scoped),
    staleTime: STALE_TIME_MS,
    gcTime: GC_TIME_MS,
    enabled
  }));
  return {
    url: data?.imageUrl ?? null,
    isLoading: enabled ? isLoading : false
  };
}